import { classNames } from '@fullstory/util/fmt';
import css from './index.module.scss';

export type RootElements = 'button' | 'div' | 'li';
export interface UseButtonOptions {
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Overrides the role for the html element.
   *
   * This is set dynamically based on the `rootElement` type.
   * if (rootElement === 'button') role = undefined
   * else role = 'button'
   */
  role?: string;

  /**
   * Overrides the tabIndex except when disabled.
   *
   * The default is based on the `rootElement` type.
   * if (rootElement === 'button') tabIndex = undefined
   * else tabIndex = 0
   */
  tabIndex?: 0 | -1;
}

export type EventHandlers = Record<string, React.EventHandler<any> | undefined>;

export interface UseButtonOptionalRootProps {
  'aria-disabled'?: React.AriaAttributes['aria-disabled'];
  disabled?: boolean;
  tabIndex?: 0 | -1;
  role?: string;
}

export interface UseButtonRootProps extends UseButtonOptionalRootProps {
  onClick: React.MouseEventHandler;
  onKeyDown: React.KeyboardEventHandler;
  onKeyUp: React.KeyboardEventHandler;
}

export interface ButtonRootClassNames {
  disabled?: string;
}

export type UseButtonReturnValue = {
  getRootProps: (otherHandlers?: EventHandlers) => UseButtonRootProps;
  getClassNames: (rootClassNames?: ButtonRootClassNames, customClassNames?: string) => string;
};

export function useButton(
  rootElement: RootElements,
  options?: UseButtonOptions,
): UseButtonReturnValue {
  const { disabled = false, role: roleOverride, tabIndex: tabIndexOverride } = options ?? {};

  // TODO: If we support anchor, input with types `button`, `submit`, or `reset` then we need to consider
  // those as native buttons.
  // TODO: we could also get more sophisticated here and use the ref to determine this as opposed to having it passed in to the hook.
  const isNativeButton = () => rootElement === 'button';

  /**
   * Custom handlers.
   */
  const createHandleClick = (otherHandlers: EventHandlers) => (event: React.MouseEvent) => {
    if (!disabled) {
      otherHandlers.onClick?.(event);
    }
  };

  const createHandleKeyDown = (otherHandlers: EventHandlers) => (event: React.KeyboardEvent) => {
    otherHandlers.onKeyDown?.(event);

    if (event.target === event.currentTarget && !isNativeButton() && event.key === ' ') {
      event.preventDefault();
    }

    // Keyboard accessibility for non interactive elements
    if (
      event.target === event.currentTarget &&
      !isNativeButton() &&
      event.key === 'Enter' &&
      !disabled
    ) {
      otherHandlers.onClick?.(event);
      event.preventDefault();
    }
  };

  const createHandleKeyUp = (otherHandlers: EventHandlers) => (event: React.KeyboardEvent) => {
    otherHandlers.onKeyUp?.(event);

    // Keyboard accessibility for non interactive elements
    if (
      event.target === event.currentTarget &&
      !isNativeButton() &&
      !disabled &&
      event.key === ' '
    ) {
      otherHandlers.onClick?.(event);
    }
  };

  /**
   * Non handler root prop object.
   */
  const otherProps: UseButtonOptionalRootProps = {};

  let tabIndex = tabIndexOverride;
  let role = roleOverride;

  if (!isNativeButton()) {
    if (!role) role = 'button';
    if (!tabIndex) tabIndex = 0;
  }

  if (tabIndex !== undefined) otherProps.tabIndex = tabIndex;
  if (role) otherProps.role = role;

  /**
   * `disabled` accessibility.
   */
  if (disabled) {
    // Only set the `disabled` attr for button elements.
    // *Other elements do support disabled (see https://www.w3schools.com/tags/att_disabled.asp), but
    // we don't allow those as rootElements.
    if (rootElement === 'button') {
      otherProps.disabled = true;
      if (otherProps.tabIndex !== undefined) delete otherProps.tabIndex;
    } else {
      otherProps.tabIndex = -1;
      otherProps['aria-disabled'] = true;
    }
  }

  function getRootProps(otherHandlers: EventHandlers = {}) {
    return {
      ...otherProps,
      ...otherHandlers,
      onClick: createHandleClick(otherHandlers),
      onKeyDown: createHandleKeyDown(otherHandlers),
      onKeyUp: createHandleKeyUp(otherHandlers),
    };
  }

  function getClassNames(rootClassNames?: ButtonRootClassNames, customClassNames?: string): string {
    return classNames(
      css.button,
      {
        buttonReset: rootElement === 'button',
        [css['disabled']]: !!disabled,
        [rootClassNames?.disabled ?? '']: !!disabled,
      },
      customClassNames,
    );
  }

  return {
    getRootProps,
    getClassNames,
  };
}
